/**
 * Mosaic Slideshow Visualizer
 *
 * Divides images into a grid of rectangles with randomized column widths and
 * row heights, then selects N non-adjacent cells to render as "pushed out"
 * depth pieces with perspective scale, shadow, and per-piece beat jitter.
 *
 * PERFORMANCE ARCHITECTURE:
 *   Two-layer rendering:
 *   - Static buffer: ALL grid cells drawn flat (recomposed on slide/layout
 *     change only — NOT per frame). Guarantees complete canvas coverage.
 *   - Per-frame depth layer: N depth cells drawn individually on top with
 *     scale-up, perspective offset, shadow, and jitter.
 *
 *   Per-frame cost: 1 drawImage (static buffer) + N depth cells
 *   (1 shadow fillRect + 1 drawImage each) ≈ 20-30 canvas ops.
 *
 * Grid is pre-calculated once at init and persists across slides. Only
 * depth cell content is re-randomized on slide change for variety.
 *
 * Supports user-imported images (drag-drop on canvas + settings slide manager)
 * and falls back to 10 procedurally generated default slides.
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'mosaic-slideshow',
  displayName: 'Mosaic Slideshow',
  contextType: '2d',
};

// Module-level instance ref so static getConfigUI render callback can access it
let _instance = null;

// Default slide canvas dimensions (scaled up during render)
const SLIDE_W = 512;
const SLIDE_H = 288;
const MAX_USER_SLIDES = 30;

class MosaicSlideshow extends Base2DVisualizer {
  static name = 'mosaic-slideshow';
  static displayName = 'Mosaic Slideshow';

  // ─── Config UI ──────────────────────────────────────────

  static getConfigUI() {
    return [
      // Slides (custom UI) — first so it's expanded by default
      {
        section: 'Slides', type: 'custom', id: 'slideManager',
        render: (container, config, setConfig) => {
          if (_instance) _instance._renderSlideManager(container, config, setConfig);
        }
      },

      // Grid
      { section: 'Grid', type: 'range', id: 'gridMinW', label: 'Min Column Width', min: 4, max: 25, default: 8, scale: 0.01 },
      { section: 'Grid', type: 'range', id: 'gridMaxW', label: 'Max Column Width', min: 10, max: 50, default: 25, scale: 0.01 },
      { section: 'Grid', type: 'range', id: 'gridMinH', label: 'Min Row Height', min: 4, max: 25, default: 8, scale: 0.01 },
      { section: 'Grid', type: 'range', id: 'gridMaxH', label: 'Max Row Height', min: 10, max: 50, default: 25, scale: 0.01 },
      { section: 'Grid', type: 'range', id: 'pieceGap', label: 'Cell Gap', min: 0, max: 8, default: 3 },
      { section: 'Grid', type: 'checkbox', id: 'enableRectangles', label: 'Mosaic Mode (off = plain slideshow)', default: true },

      // Depth
      { section: 'Depth', type: 'range', id: 'depthCount', label: 'Pushed Out Cells', min: 0, max: 30, default: 10 },
      { section: 'Depth', type: 'range', id: 'depthAmount', label: 'Depth Amount', min: 0, max: 20, default: 10 },
      { section: 'Depth', type: 'checkbox', id: 'showShadows', label: 'Show Shadows', default: true },
      { section: 'Depth', type: 'checkbox', id: 'enableRandomize', label: 'Randomize Depth Pieces', default: true },

      // Audio
      { section: 'Audio', type: 'range', id: 'audioReactivity', label: 'Audio Reactivity', min: 0, max: 100, default: 60, scale: 0.01 },
      { section: 'Audio', type: 'range', id: 'jitterIntensity', label: 'Beat Jitter', min: 0, max: 100, default: 60, scale: 0.01 },
      { section: 'Audio', type: 'range', id: 'blurIntensity', label: 'Beat Blur', min: 0, max: 100, default: 30, scale: 0.01 },
      { section: 'Audio', type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },

      // Slideshow
      { section: 'Slideshow', type: 'range', id: 'slideInterval', label: 'Slide Duration (sec)', min: 3, max: 30, default: 10 },
      { section: 'Slideshow', type: 'select', id: 'transitionMode', label: 'Transition', default: 'cut',
        options: [
          { value: 'cut', label: 'Cut' },
          { value: 'crossfade', label: 'Crossfade' },
        ]
      },

      // Effects
      { section: 'Effects', type: 'checkbox', id: 'enableTint', label: 'Color Tint', default: false },
      { section: 'Effects', type: 'color', id: 'tintColor', label: 'Tint Color', default: '#ff6600' },
      { section: 'Effects', type: 'range', id: 'tintOpacity', label: 'Tint Opacity', min: 0, max: 50, default: 15, scale: 0.01 },
      { section: 'Effects', type: 'checkbox', id: 'enableScalePulse', label: 'Scale Pulse', default: false },
      { section: 'Effects', type: 'range', id: 'scalePulseAmount', label: 'Pulse Amount', min: 0, max: 20, default: 8, scale: 0.01 },
      { section: 'Effects', type: 'checkbox', id: 'enableVignette', label: 'Vignette', default: true },
      { section: 'Effects', type: 'range', id: 'vignetteStrength', label: 'Vignette Strength', min: 0, max: 100, default: 30, scale: 0.01 },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 32;
  }

  // ─── Lifecycle ──────────────────────────────────────────

  init() {
    _instance = this;

    // Slide state
    this._defaultSlides = [];   // procedurally generated canvases
    this._userSlides = [];      // { canvas, name } imported by user
    this._slideIndex = 0;
    this._prevSlideIndex = -1;
    this._slideTimer = 0;
    this._crossfadeProgress = 0; // 0 = showing prev, 1 = showing current
    this._isCrossfading = false;

    // Grid layout
    this._cells = [];      // flat array of cell objects
    this._gridCols = 0;    // number of columns
    this._gridRows = 0;    // number of rows

    // Pre-composited static buffer (ALL cells flat)
    this._staticBuffer = null;
    this._staticBufferCtx = null;
    this._prevStaticBuffer = null;   // for crossfade blending
    this._prevStaticBufferCtx = null;
    this._staticBufferW = 0;
    this._staticBufferH = 0;

    // Slide refs for per-frame depth rendering
    this._currentSlide = null;
    this._prevSlide = null;

    // Audio reactivity state (blur + scale are global; jitter is per-piece)
    this._blurDecay = 0;
    this._scalePulseDecay = 0;
    this._lastTime = performance.now();

    // Vignette cache
    this._vignetteGradient = null;
    this._vignetteCacheW = 0;
    this._vignetteCacheH = 0;

    // Drop overlay
    this._dropOverlay = null;
    this._dropOverlayTimer = null;

    // Generate default slides
    this._generateDefaultSlides();

    // Generate initial grid + compose buffer
    this._generateGrid();

    // Create drop overlay (deferred so canvas is in DOM)
    requestAnimationFrame(() => this._createDropOverlay());
  }

  dispose() {
    _instance = null;
    this._hideDropOverlay();
    if (this._dropOverlay && this._dropOverlay.parentElement) {
      this._dropOverlay.parentElement.removeChild(this._dropOverlay);
    }
    this._dropOverlay = null;
    if (this._dropOverlayTimer) clearTimeout(this._dropOverlayTimer);
    this._defaultSlides = [];
    this._userSlides = [];
    this._cells = [];
    this._staticBuffer = null;
    this._staticBufferCtx = null;
    this._prevStaticBuffer = null;
    this._prevStaticBufferCtx = null;
    this._currentSlide = null;
    this._prevSlide = null;
    this._vignetteGradient = null;
  }

  clear() {
    super.clear();
    this._slideTimer = 0;
    this._crossfadeProgress = 0;
    this._isCrossfading = false;
    this._blurDecay = 0;
    this._scalePulseDecay = 0;
    this._lastTime = performance.now();
    this._generateGrid();
  }

  onConfigChange(changed) {
    // Grid structure params: full grid regeneration
    const gridKeys = ['gridMinW', 'gridMaxW', 'gridMinH', 'gridMaxH', 'enableRectangles'];
    if (gridKeys.some(k => changed[k] !== undefined)) {
      this._generateGrid();
      return;
    }

    // Depth selection params: re-select + re-randomize + recompose
    if (changed.depthCount !== undefined) {
      this._selectDepthCells();
      this._randomizeDepthContent();
      this._composeStaticLayer();
      return;
    }

    // Visual-only: just recompose
    const visualKeys = ['pieceGap', 'depthAmount', 'showShadows', 'enableRandomize'];
    if (visualKeys.some(k => changed[k] !== undefined)) {
      if (changed.enableRandomize !== undefined) {
        this._randomizeDepthContent();
      }
      this._composeStaticLayer();
    }
  }

  // ─── Core Rendering (per frame — fast path) ────────────

  renderFrame(amplitudes, features) {
    const now = performance.now();
    const dt = Math.min((now - this._lastTime) / 1000, 0.1); // seconds, capped
    this._lastTime = now;

    const slides = this._getCurrentSlides();
    if (slides.length === 0) return;

    const { ctx, canvas } = this;
    const cw = canvas.width;
    const ch = canvas.height;
    const cfg = this.config;

    // ── 1. Update slide timer ──
    this._slideTimer += dt;
    if (this._slideTimer >= cfg.slideInterval) {
      this._advanceSlide();
      this._slideTimer = 0;
    }

    // ── 2. Update crossfade ──
    if (this._isCrossfading) {
      const fadeDuration = 0.8; // seconds
      this._crossfadeProgress += dt / fadeDuration;
      if (this._crossfadeProgress >= 1) {
        this._crossfadeProgress = 1;
        this._isCrossfading = false;
      }
    }

    // ── 3. Decay audio effects ──
    // Per-piece jitter decay (each depth cell decays independently)
    const cells = this._cells;
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (!c.isDepth) continue;
      c.jitterX *= 0.88;
      c.jitterY *= 0.88;
    }
    this._blurDecay *= 0.92;
    this._scalePulseDecay *= 0.90;

    // ── 4. Beat triggers ──
    const reactivity = cfg.audioReactivity;
    if (this.beatDetected && reactivity > 0) {
      const confidence = this.beatConfidence;
      // Per-piece jitter for depth cells
      const jitterAmt = cfg.jitterIntensity * reactivity * 60;
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        if (!c.isDepth) continue;
        c.jitterX = (Math.random() - 0.5) * jitterAmt * confidence;
        c.jitterY = (Math.random() - 0.5) * jitterAmt * confidence;
      }
      // Blur
      this._blurDecay = cfg.blurIntensity * reactivity * confidence * 3;
      // Scale pulse
      if (cfg.enableScalePulse) {
        this._scalePulseDecay = cfg.scalePulseAmount * reactivity * confidence * 2;
      }
    }

    // ── 5. Ensure buffers match canvas size ──
    this._ensureStaticBuffer(cw, ch);

    // ── 6. Render ──
    if (!cfg.enableRectangles) {
      const currentSlide = slides[this._slideIndex % slides.length];
      const prevSlide = this._prevSlideIndex >= 0 ? slides[this._prevSlideIndex % slides.length] : null;
      this._drawPlainSlide(ctx, cw, ch, currentSlide, prevSlide);
    } else {
      this._drawMosaicFromBuffer(ctx, cw, ch);
    }

    // ── 7. Post-processing ──
    if (cfg.enableTint) {
      this._drawTintOverlay(ctx, cw, ch);
    }
    if (cfg.enableVignette) {
      this._drawVignette(ctx, cw, ch);
    }
  }

  // ─── Plain Slideshow (no mosaic) ───────────────────────

  _drawPlainSlide(ctx, cw, ch, currentSlide, prevSlide) {
    if (this._isCrossfading && prevSlide) {
      ctx.globalAlpha = 1 - this._crossfadeProgress;
      ctx.drawImage(prevSlide, 0, 0, cw, ch);
      ctx.globalAlpha = this._crossfadeProgress;
    }
    ctx.drawImage(currentSlide, 0, 0, cw, ch);
    ctx.globalAlpha = 1;
  }

  // ─── Static Buffer Management ─────────────────────────

  _ensureStaticBuffer(w, h) {
    if (this._staticBuffer && this._staticBufferW === w && this._staticBufferH === h) return;

    this._staticBuffer = document.createElement('canvas');
    this._staticBuffer.width = w;
    this._staticBuffer.height = h;
    this._staticBufferCtx = this._staticBuffer.getContext('2d');

    this._prevStaticBuffer = document.createElement('canvas');
    this._prevStaticBuffer.width = w;
    this._prevStaticBuffer.height = h;
    this._prevStaticBufferCtx = this._prevStaticBuffer.getContext('2d');

    this._staticBufferW = w;
    this._staticBufferH = h;

    this._composeStaticLayer();
  }

  /**
   * Pre-composite ALL cells flat onto _staticBuffer.
   * Called on slide change, layout change, or buffer resize — NOT per frame.
   * Stores slide refs for per-frame depth rendering.
   */
  _composeStaticLayer() {
    if (!this._staticBuffer) return;

    const slides = this._getCurrentSlides();
    if (slides.length === 0) return;

    const cw = this._staticBufferW;
    const ch = this._staticBufferH;

    // Store slide refs for per-frame depth rendering
    this._currentSlide = slides[this._slideIndex % slides.length];
    this._prevSlide = this._prevSlideIndex >= 0
      ? slides[this._prevSlideIndex % slides.length]
      : null;

    // Compose current slide (all cells flat)
    this._composeStaticToBuffer(this._staticBufferCtx, cw, ch, this._currentSlide);

    // Compose previous slide (for crossfade)
    if (this._prevSlide) {
      this._composeStaticToBuffer(this._prevStaticBufferCtx, cw, ch, this._prevSlide);
    }
  }

  /**
   * Draw ALL cells flat to a buffer. Every cell uses its original grid
   * position as the source region, ensuring complete canvas coverage
   * with no gaps (depth cells are drawn again per-frame on top).
   */
  _composeStaticToBuffer(bufCtx, cw, ch, slide) {
    const gap = this.config.pieceGap;

    bufCtx.clearRect(0, 0, cw, ch);

    for (const cell of this._cells) {
      const dx = cell.x * cw;
      const dy = cell.y * ch;
      const dw = cell.w * cw - gap;
      const dh = cell.h * ch - gap;
      if (dw <= 0 || dh <= 0) continue;

      // Source from original grid position (not randomized src*)
      const sx = cell.x * slide.width;
      const sy = cell.y * slide.height;
      const sw = cell.w * slide.width;
      const sh = cell.h * slide.height;
      if (sw <= 0 || sh <= 0) continue;

      bufCtx.drawImage(slide, sx, sy, sw, sh, dx, dy, dw, dh);
    }
  }

  /**
   * Per-frame: draw depth cells on top of the static buffer.
   * Each cell is scaled up from its center and offset for perspective.
   *
   * Rendering order:
   *   1. Shadows for all depth cells
   *   2. Depth cell images (with crossfade if active)
   */
  _drawDepthPiecesPerFrame(ctx, cw, ch) {
    const cells = this._cells;
    const cfg = this.config;
    const depth = cfg.depthAmount;
    if (depth <= 0) return;

    const gap = cfg.pieceGap;
    const showShadows = cfg.showShadows;
    const scale = 1 + depth * 0.015; // depth=10 → 1.15x
    const isCrossfading = this._isCrossfading;
    const progress = this._crossfadeProgress;
    const currentSlide = this._currentSlide;
    const prevSlide = this._prevSlide;

    // Helper: draw one depth cell from a slide
    const drawCell = (cell, slide, ox, oy) => {
      const baseW = cell.w * cw - gap;
      const baseH = cell.h * ch - gap;
      if (baseW <= 0 || baseH <= 0) return;

      // Scaled size
      const sw = baseW * scale;
      const sh = baseH * scale;

      // Cell center in canvas coords
      const cx = cell.x * cw + baseW / 2;
      const cy = cell.y * ch + baseH / 2;

      // Draw position: centered + perspective + jitter
      const dx = cx - sw / 2 + ox;
      const dy = cy - sh / 2 + oy;

      // Source from slide (randomized for depth cells)
      const srcX = cell.srcX * slide.width;
      const srcY = cell.srcY * slide.height;
      const srcW = cell.srcW * slide.width;
      const srcH = cell.srcH * slide.height;
      if (srcW <= 0 || srcH <= 0) return;

      ctx.drawImage(slide, srcX, srcY, srcW, srcH, dx, dy, sw, sh);
    };

    // ── Layer 1: shadows ──
    if (showShadows) {
      const shadowOff = depth * 0.6;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      for (const cell of cells) {
        if (!cell.isDepth) continue;
        const baseW = cell.w * cw - gap;
        const baseH = cell.h * ch - gap;
        if (baseW <= 0 || baseH <= 0) continue;
        const sw = baseW * scale;
        const sh = baseH * scale;
        const cx = cell.x * cw + baseW / 2;
        const cy = cell.y * ch + baseH / 2;
        const ox = cell.jitterX + cell.perspectiveX * cw;
        const oy = cell.jitterY + cell.perspectiveY * ch;
        ctx.fillRect(cx - sw / 2 + ox + shadowOff, cy - sh / 2 + oy + shadowOff, sw, sh);
      }
    }

    // ── Layer 2: depth cell images ──
    for (const cell of cells) {
      if (!cell.isDepth) continue;
      const ox = cell.jitterX + cell.perspectiveX * cw;
      const oy = cell.jitterY + cell.perspectiveY * ch;

      if (isCrossfading && prevSlide) {
        ctx.globalAlpha = 1 - progress;
        drawCell(cell, prevSlide, ox, oy);
        ctx.globalAlpha = progress;
      }
      if (currentSlide) {
        drawCell(cell, currentSlide, ox, oy);
      }
      if (isCrossfading && prevSlide) {
        ctx.globalAlpha = 1;
      }
    }
  }

  /**
   * Per-frame: draw static buffer + depth layer to main canvas.
   * Global blur and scale pulse wrap everything.
   */
  _drawMosaicFromBuffer(ctx, cw, ch) {
    const blurVal = Math.min(this._blurDecay, 3);
    const scalePulse = this._scalePulseDecay;

    if (blurVal > 0.2) {
      ctx.filter = `blur(${blurVal.toFixed(1)}px)`;
    }

    const hasScale = scalePulse > 0.001;
    if (hasScale) {
      ctx.save();
      ctx.translate(cw / 2, ch / 2);
      const s = 1 + scalePulse;
      ctx.scale(s, s);
      ctx.translate(-cw / 2, -ch / 2);
    }

    // Static layer (all cells flat) with crossfade
    if (this._isCrossfading && this._prevSlideIndex >= 0) {
      ctx.globalAlpha = 1 - this._crossfadeProgress;
      ctx.drawImage(this._prevStaticBuffer, 0, 0);
      ctx.globalAlpha = this._crossfadeProgress;
    }
    ctx.drawImage(this._staticBuffer, 0, 0);
    ctx.globalAlpha = 1;

    // Depth layer (pushed-out cells with jitter + perspective)
    this._drawDepthPiecesPerFrame(ctx, cw, ch);

    if (hasScale) {
      ctx.restore();
    }
    if (blurVal > 0.2) {
      ctx.filter = 'none';
    }
  }

  // ─── Post-processing ───────────────────────────────────

  _drawTintOverlay(ctx, cw, ch) {
    const color = this.config.tintColor;
    const base = this.config.tintOpacity;
    const modulated = base + this.midEnergy * 0.1 * this.config.audioReactivity;
    ctx.fillStyle = color;
    ctx.globalAlpha = Math.min(modulated, 0.5);
    ctx.fillRect(0, 0, cw, ch);
    ctx.globalAlpha = 1;
  }

  _drawVignette(ctx, cw, ch) {
    if (!this._vignetteGradient || this._vignetteCacheW !== cw || this._vignetteCacheH !== ch) {
      const cx = cw / 2;
      const cy = ch / 2;
      const r = Math.sqrt(cx * cx + cy * cy);
      const grad = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,1)');
      this._vignetteGradient = grad;
      this._vignetteCacheW = cw;
      this._vignetteCacheH = ch;
    }
    ctx.globalAlpha = this.config.vignetteStrength;
    ctx.fillStyle = this._vignetteGradient;
    ctx.fillRect(0, 0, cw, ch);
    ctx.globalAlpha = 1;
  }

  // ─── Grid Generation ──────────────────────────────────

  /**
   * Generate the complete grid layout:
   *   1. Random column widths + row heights → grid of cells
   *   2. Select N non-adjacent cells as depth
   *   3. Randomize depth cell content
   *   4. Compose static buffer
   */
  _generateGrid() {
    const cfg = this.config;

    // Generate random column widths and row heights (normalized to sum = 1.0)
    const colWidths = this._generateSegments(cfg.gridMinW, cfg.gridMaxW);
    const rowHeights = this._generateSegments(cfg.gridMinH, cfg.gridMaxH);

    this._gridCols = colWidths.length;
    this._gridRows = rowHeights.length;

    // Build cell grid
    const cells = [];
    let y = 0;
    for (let r = 0; r < rowHeights.length; r++) {
      let x = 0;
      for (let c = 0; c < colWidths.length; c++) {
        cells.push({
          x, y,
          w: colWidths[c],
          h: rowHeights[r],
          row: r, col: c,
          isDepth: false,
          // Source region: defaults to own grid position (1:1 mapping)
          srcX: x, srcY: y,
          srcW: colWidths[c], srcH: rowHeights[r],
          jitterX: 0, jitterY: 0,
          perspectiveX: 0, perspectiveY: 0,
        });
        x += colWidths[c];
      }
      y += rowHeights[r];
    }
    this._cells = cells;

    // Select depth cells + randomize + compose
    this._selectDepthCells();
    this._randomizeDepthContent();
    this._composeStaticLayer();
  }

  /**
   * Generate random segment sizes between min and max that partition [0, 1].
   * Last segment absorbs any remainder.
   */
  _generateSegments(min, max) {
    // Clamp max to be at least min
    const effectiveMax = Math.max(min, max);
    const segments = [];
    let pos = 0;

    while (pos < 1.0) {
      const remaining = 1.0 - pos;

      // If remaining fits in one segment, take it all
      if (remaining <= effectiveMax) {
        segments.push(remaining);
        break;
      }

      // Ensure room for at least one more segment after this one
      const maxW = Math.min(effectiveMax, remaining - min);
      if (maxW < min) {
        // Can't fit another segment — take all remaining
        segments.push(remaining);
        break;
      }

      const w = min + Math.random() * (maxW - min);
      segments.push(w);
      pos += w;
    }

    return segments;
  }

  /**
   * Randomly select N cells as depth (pushed out), with the constraint
   * that no two adjacent cells (sharing an edge) are both selected.
   * Uses greedy random selection from a shuffled candidate list.
   */
  _selectDepthCells() {
    const cells = this._cells;
    const cols = this._gridCols;
    const rows = this._gridRows;
    const count = this.config.depthCount;

    // Reset all cells
    for (const c of cells) {
      c.isDepth = false;
      c.jitterX = 0;
      c.jitterY = 0;
      c.perspectiveX = 0;
      c.perspectiveY = 0;
    }

    if (count <= 0 || cells.length === 0) return;

    // Build shuffled index list
    const indices = Array.from({ length: cells.length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }

    // Greedy selection: pick cells, exclude their neighbors
    const excluded = new Set();
    let selected = 0;

    for (const idx of indices) {
      if (selected >= count) break;
      if (excluded.has(idx)) continue;

      cells[idx].isDepth = true;
      selected++;

      // Exclude this cell and all 4 neighbors
      excluded.add(idx);
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      if (row > 0)        excluded.add((row - 1) * cols + col);
      if (row < rows - 1) excluded.add((row + 1) * cols + col);
      if (col > 0)        excluded.add(row * cols + (col - 1));
      if (col < cols - 1) excluded.add(row * cols + (col + 1));
    }

    // Compute perspective offsets for selected depth cells
    this._computePerspectiveOffsets();
  }

  /**
   * Shuffle source regions among depth cells only.
   * Non-depth cells keep their original 1:1 grid mapping.
   */
  _randomizeDepthContent() {
    // Reset non-depth cells to 1:1 mapping
    for (const c of this._cells) {
      if (!c.isDepth) {
        c.srcX = c.x; c.srcY = c.y;
        c.srcW = c.w; c.srcH = c.h;
      }
    }

    if (!this.config.enableRandomize) {
      // Even depth cells get 1:1 if randomization is off
      for (const c of this._cells) {
        c.srcX = c.x; c.srcY = c.y;
        c.srcW = c.w; c.srcH = c.h;
      }
      return;
    }

    const depthCells = this._cells.filter(c => c.isDepth);
    if (depthCells.length <= 1) {
      for (const c of depthCells) {
        c.srcX = c.x; c.srcY = c.y;
        c.srcW = c.w; c.srcH = c.h;
      }
      return;
    }

    // Collect source regions, shuffle, reassign
    const sources = depthCells.map(c => ({ x: c.x, y: c.y, w: c.w, h: c.h }));
    for (let i = sources.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [sources[i], sources[j]] = [sources[j], sources[i]];
    }
    for (let i = 0; i < depthCells.length; i++) {
      depthCells[i].srcX = sources[i].x;
      depthCells[i].srcY = sources[i].y;
      depthCells[i].srcW = sources[i].w;
      depthCells[i].srcH = sources[i].h;
    }
  }

  /**
   * Compute perspective (parallax) offsets for depth cells.
   * Pieces near edges get pushed outward from center; center pieces stay put.
   */
  _computePerspectiveOffsets() {
    const depth = this.config.depthAmount;
    for (const c of this._cells) {
      if (!c.isDepth) continue;
      const centerX = c.x + c.w / 2;
      const centerY = c.y + c.h / 2;
      // Factor chosen so edge cells get ~20px offset at depth=10 on 1400px canvas
      // (0.5 * 10 * 0.003 * 1400 = 21px)
      c.perspectiveX = (centerX - 0.5) * depth * 0.003;
      c.perspectiveY = (centerY - 0.5) * depth * 0.003;
    }
  }

  // ─── Slide Management ──────────────────────────────────

  _getCurrentSlides() {
    if (this._userSlides.length > 0) {
      return this._userSlides.map(s => s.canvas);
    }
    return this._defaultSlides;
  }

  _advanceSlide() {
    const slides = this._getCurrentSlides();
    if (slides.length <= 1) return;

    this._prevSlideIndex = this._slideIndex;
    this._slideIndex = (this._slideIndex + 1) % slides.length;

    if (this.config.transitionMode === 'crossfade') {
      this._isCrossfading = true;
      this._crossfadeProgress = 0;
    }

    // Grid structure stays fixed. Just re-randomize depth content + recompose.
    this._randomizeDepthContent();
    this._composeStaticLayer();
  }

  _importImages(files) {
    const remaining = MAX_USER_SLIDES - this._userSlides.length;
    const toProcess = Array.from(files).slice(0, remaining);
    if (toProcess.length === 0) return;

    let loaded = 0;
    const totalValid = toProcess.filter(f => f.type.startsWith('image/')).length;
    if (totalValid === 0) return;

    for (const file of toProcess) {
      if (!file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = SLIDE_W;
          canvas.height = SLIDE_H;
          const ctx = canvas.getContext('2d');
          // Cover-fit
          const scale = Math.max(SLIDE_W / img.width, SLIDE_H / img.height);
          const sw = SLIDE_W / scale;
          const sh = SLIDE_H / scale;
          const sx = (img.width - sw) / 2;
          const sy = (img.height - sh) / 2;
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, SLIDE_W, SLIDE_H);

          this._userSlides.push({ canvas, name: file.name });
          loaded++;
          if (loaded === totalValid) {
            this._slideIndex = 0;
            this._slideTimer = 0;
            this._generateGrid();
            this._hideDropOverlay();
          }
        };
        img.onerror = () => {
          console.warn(`[MosaicSlideshow] Failed to decode image: ${file.name}`);
          loaded++;
          if (loaded === totalValid) {
            this._slideIndex = 0;
            this._slideTimer = 0;
            this._generateGrid();
            this._hideDropOverlay();
          }
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        console.warn(`[MosaicSlideshow] Failed to read file: ${file.name}`);
        loaded++;
        if (loaded === totalValid) {
          this._slideIndex = 0;
          this._slideTimer = 0;
          this._generateGrid();
          this._hideDropOverlay();
        }
      };
      reader.readAsDataURL(file);
    }
  }

  _removeSlide(index) {
    if (index < 0 || index >= this._userSlides.length) return;
    this._userSlides.splice(index, 1);
    if (this._slideIndex >= this._userSlides.length) {
      this._slideIndex = 0;
    }
    this._slideTimer = 0;
    this._composeStaticLayer();
  }

  _clearUserSlides() {
    this._userSlides = [];
    this._slideIndex = 0;
    this._slideTimer = 0;
    this._composeStaticLayer();
  }

  _reorderSlides(fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    if (fromIdx < 0 || toIdx < 0) return;
    if (fromIdx >= this._userSlides.length || toIdx >= this._userSlides.length) return;
    const [item] = this._userSlides.splice(fromIdx, 1);
    this._userSlides.splice(toIdx, 0, item);
  }

  // ─── Drop Overlay ──────────────────────────────────────

  _createDropOverlay() {
    if (!this.canvas || !this.canvas.parentElement) return;
    if (this.canvas.dataset.exporting) return;

    const overlay = document.createElement('div');
    overlay.className = 'mosaic-drop-overlay';
    overlay.innerHTML = `
      <div class="mosaic-drop-card">
        <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
        <div class="mosaic-drop-card-title">Drop images for slideshow</div>
        <div class="mosaic-drop-card-subtitle">or add via settings</div>
        <button class="mosaic-drop-card-dismiss">Use defaults</button>
      </div>
    `;

    overlay.querySelector('.mosaic-drop-card-dismiss').addEventListener('click', (e) => {
      e.stopPropagation();
      this._hideDropOverlay();
    });

    overlay.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      overlay.classList.add('drag-active');
    });
    overlay.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      overlay.classList.remove('drag-active');
    });
    overlay.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      overlay.classList.remove('drag-active');
      if (e.dataTransfer?.files?.length) {
        this._importImages(e.dataTransfer.files);
      }
    });

    this.canvas.parentElement.appendChild(overlay);
    this._dropOverlay = overlay;

    // Auto-hide after 8 seconds
    this._dropOverlayTimer = setTimeout(() => this._hideDropOverlay(), 8000);
  }

  _hideDropOverlay() {
    if (this._dropOverlayTimer) {
      clearTimeout(this._dropOverlayTimer);
      this._dropOverlayTimer = null;
    }
    if (this._dropOverlay) {
      this._dropOverlay.classList.add('hidden');
    }
  }

  // ─── Settings Slide Manager ────────────────────────────

  _renderSlideManager(container, config, setConfig) {
    container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'mosaic-slide-manager';

    if (this._userSlides.length === 0) {
      this._renderSlideManagerEmpty(wrapper);
    } else {
      this._renderSlideManagerPopulated(wrapper);
    }

    container.appendChild(wrapper);
  }

  _renderSlideManagerEmpty(wrapper) {
    const dropzone = document.createElement('div');
    dropzone.className = 'mosaic-slide-dropzone';
    dropzone.innerHTML = `<span>Drop images or click to add</span>`;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        this._importImages(e.target.files);
        const container = document.getElementById('slideManager-container');
        if (container) this._renderSlideManager(container);
      }
    });
    dropzone.appendChild(fileInput);

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('drag-active');
    });
    dropzone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dropzone.classList.remove('drag-active');
    });
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('drag-active');
      if (e.dataTransfer?.files?.length) {
        this._importImages(e.dataTransfer.files);
        const container = document.getElementById('slideManager-container');
        if (container) this._renderSlideManager(container);
      }
    });

    wrapper.appendChild(dropzone);
  }

  _renderSlideManagerPopulated(wrapper) {
    const list = document.createElement('div');
    list.className = 'mosaic-slide-list';

    this._userSlides.forEach((slide, index) => {
      const item = document.createElement('div');
      item.className = 'mosaic-slide-item';
      item.draggable = true;
      item.dataset.index = index;

      const thumb = document.createElement('canvas');
      thumb.className = 'mosaic-slide-item-thumb';
      thumb.width = 40;
      thumb.height = 24;
      const thumbCtx = thumb.getContext('2d');
      thumbCtx.drawImage(slide.canvas, 0, 0, 40, 24);

      const name = document.createElement('span');
      name.className = 'mosaic-slide-item-name';
      name.textContent = slide.name;

      const removeBtn = document.createElement('button');
      removeBtn.className = 'mosaic-slide-item-remove';
      removeBtn.textContent = '\u00D7';
      removeBtn.title = 'Remove slide';
      removeBtn.addEventListener('click', () => {
        this._removeSlide(index);
        const container = document.getElementById('slideManager-container');
        if (container) this._renderSlideManager(container);
      });

      item.appendChild(thumb);
      item.appendChild(name);
      item.appendChild(removeBtn);

      item.addEventListener('dragstart', (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        item.classList.add('dragging');
      });
      item.addEventListener('dragend', () => {
        item.classList.remove('dragging');
      });
      item.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      });
      item.addEventListener('drop', (e) => {
        e.preventDefault();
        const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
        const toIdx = index;
        this._reorderSlides(fromIdx, toIdx);
        const container = document.getElementById('slideManager-container');
        if (container) this._renderSlideManager(container);
      });

      list.appendChild(item);
    });

    wrapper.appendChild(list);

    const actions = document.createElement('div');
    actions.className = 'mosaic-slide-actions';

    const addBtn = document.createElement('button');
    addBtn.textContent = '+ Add More';
    addBtn.addEventListener('click', () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.addEventListener('change', (e) => {
        if (e.target.files.length > 0) {
          this._importImages(e.target.files);
          const container = document.getElementById('slideManager-container');
          if (container) this._renderSlideManager(container);
        }
      });
      input.click();
    });

    const clearBtn = document.createElement('button');
    clearBtn.className = 'mosaic-slide-clear';
    clearBtn.textContent = 'Clear All';
    clearBtn.addEventListener('click', () => {
      this._clearUserSlides();
      const container = document.getElementById('slideManager-container');
      if (container) this._renderSlideManager(container);
    });

    actions.appendChild(addBtn);
    actions.appendChild(clearBtn);
    wrapper.appendChild(actions);
  }

  // ─── Procedural Default Slides ─────────────────────────

  _generateDefaultSlides() {
    this._defaultSlides = [];
    const generators = [
      this._genGradientSweep,
      this._genConcentricRings,
      this._genDiagonalStripes,
      this._genColorNoise,
      this._genGeometricComposition,
      this._genRadialBurst,
      this._genBWMarble,
      this._genBWHalftone,
      this._genBWBlocks,
      this._genBWInkWash,
    ];

    for (const gen of generators) {
      const canvas = document.createElement('canvas');
      canvas.width = SLIDE_W;
      canvas.height = SLIDE_H;
      const ctx = canvas.getContext('2d');
      gen.call(this, ctx, SLIDE_W, SLIDE_H);
      this._defaultSlides.push(canvas);
    }
  }

  _genGradientSweep(ctx, w, h) {
    const angle = Math.random() * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const grad = ctx.createLinearGradient(
      w / 2 - cos * w, h / 2 - sin * h,
      w / 2 + cos * w, h / 2 + sin * h
    );
    const stops = 3 + Math.floor(Math.random() * 2);
    for (let i = 0; i < stops; i++) {
      grad.addColorStop(i / (stops - 1), this._randColor());
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }

  _genConcentricRings(ctx, w, h) {
    const cx = w / 2;
    const cy = h / 2;
    const maxR = Math.max(w, h) * 0.7;
    const ringCount = 6 + Math.floor(Math.random() * 6);
    for (let i = ringCount; i >= 0; i--) {
      const r = (maxR / ringCount) * i;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = this._randColor();
      ctx.fill();
    }
  }

  _genDiagonalStripes(ctx, w, h) {
    const stripeCount = 5 + Math.floor(Math.random() * 5);
    const stripeW = (w + h) / stripeCount;
    ctx.fillStyle = this._randColor();
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < stripeCount; i++) {
      ctx.fillStyle = this._randColor();
      ctx.beginPath();
      const x = i * stripeW - h;
      ctx.moveTo(x, 0);
      ctx.lineTo(x + stripeW * 0.6, 0);
      ctx.lineTo(x + stripeW * 0.6 + h, h);
      ctx.lineTo(x + h, h);
      ctx.closePath();
      ctx.fill();
    }
  }

  _genColorNoise(ctx, w, h) {
    const blockSize = 30 + Math.floor(Math.random() * 40);
    for (let y = 0; y < h; y += blockSize) {
      for (let x = 0; x < w; x += blockSize) {
        ctx.fillStyle = this._randColor();
        ctx.fillRect(x, y, blockSize, blockSize);
      }
    }
  }

  _genGeometricComposition(ctx, w, h) {
    ctx.fillStyle = this._randColor();
    ctx.fillRect(0, 0, w, h);
    const shapeCount = 8 + Math.floor(Math.random() * 8);
    for (let i = 0; i < shapeCount; i++) {
      ctx.fillStyle = this._randColor(0.7);
      if (Math.random() > 0.5) {
        const rw = 40 + Math.random() * 200;
        const rh = 40 + Math.random() * 150;
        ctx.fillRect(Math.random() * w - rw / 2, Math.random() * h - rh / 2, rw, rh);
      } else {
        ctx.beginPath();
        ctx.moveTo(Math.random() * w, Math.random() * h);
        ctx.lineTo(Math.random() * w, Math.random() * h);
        ctx.lineTo(Math.random() * w, Math.random() * h);
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  _genRadialBurst(ctx, w, h) {
    const cx = Math.random() * w;
    const cy = Math.random() * h;
    const rayCount = 12 + Math.floor(Math.random() * 12);
    const maxR = Math.max(w, h) * 1.2;
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < rayCount; i++) {
      const a1 = (i / rayCount) * Math.PI * 2;
      const a2 = ((i + 0.5) / rayCount) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a1) * maxR, cy + Math.sin(a1) * maxR);
      ctx.lineTo(cx + Math.cos(a2) * maxR, cy + Math.sin(a2) * maxR);
      ctx.closePath();
      ctx.fillStyle = i % 2 === 0 ? this._randWarm() : this._randCool();
      ctx.fill();
    }
  }

  _genBWMarble(ctx, w, h) {
    ctx.fillStyle = '#888';
    ctx.fillRect(0, 0, w, h);
    const layers = 6;
    for (let i = 0; i < layers; i++) {
      const x1 = Math.random() * w;
      const y1 = Math.random() * h;
      const x2 = Math.random() * w;
      const y2 = Math.random() * h;
      const grad = ctx.createLinearGradient(x1, y1, x2, y2);
      const g1 = Math.floor(Math.random() * 255);
      const g2 = Math.floor(Math.random() * 255);
      grad.addColorStop(0, `rgba(${g1},${g1},${g1},0.4)`);
      grad.addColorStop(0.5, `rgba(${g2},${g2},${g2},0.3)`);
      grad.addColorStop(1, `rgba(${g1},${g1},${g1},0.4)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    }
  }

  _genBWHalftone(ctx, w, h) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    const dotSpacing = 12;
    ctx.fillStyle = '#000';
    for (let y = 0; y < h; y += dotSpacing) {
      for (let x = 0; x < w; x += dotSpacing) {
        const t = x / w;
        const radius = t * (dotSpacing * 0.45);
        if (radius < 0.5) continue;
        ctx.beginPath();
        ctx.arc(x + dotSpacing / 2, y + dotSpacing / 2, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  _genBWBlocks(ctx, w, h) {
    ctx.fillStyle = '#ddd';
    ctx.fillRect(0, 0, w, h);
    const count = 15 + Math.floor(Math.random() * 15);
    for (let i = 0; i < count; i++) {
      const gray = Math.floor(Math.random() * 200);
      ctx.fillStyle = `rgb(${gray},${gray},${gray})`;
      const bw = 30 + Math.random() * 180;
      const bh = 30 + Math.random() * 120;
      ctx.fillRect(Math.random() * w - bw / 2, Math.random() * h - bh / 2, bw, bh);
    }
  }

  _genBWInkWash(ctx, w, h) {
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, w, h);
    const blobs = 8 + Math.floor(Math.random() * 8);
    for (let i = 0; i < blobs; i++) {
      const bx = Math.random() * w;
      const by = Math.random() * h;
      const r = 40 + Math.random() * 120;
      const gray = Math.floor(40 + Math.random() * 160);
      const grad = ctx.createRadialGradient(bx, by, 0, bx, by, r);
      grad.addColorStop(0, `rgba(${gray},${gray},${gray},0.6)`);
      grad.addColorStop(1, `rgba(${gray},${gray},${gray},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(bx - r, by - r, r * 2, r * 2);
    }
  }

  // --- Color helpers ---

  _randColor(alpha = 1) {
    const h = Math.floor(Math.random() * 360);
    const s = 60 + Math.floor(Math.random() * 40);
    const l = 40 + Math.floor(Math.random() * 30);
    return alpha < 1 ? `hsla(${h},${s}%,${l}%,${alpha})` : `hsl(${h},${s}%,${l}%)`;
  }

  _randWarm() {
    const h = Math.floor(Math.random() * 60) + 350;
    return `hsl(${h % 360},80%,55%)`;
  }

  _randCool() {
    const h = 180 + Math.floor(Math.random() * 80);
    return `hsl(${h},70%,50%)`;
  }
}

// Register with the visualizer system
VisualizerRegistry.register(MosaicSlideshow);
